import {
  APP_INITIALIZER,
  ErrorHandler,
  importProvidersFrom,
  inject,
  NgModule,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { MatNativeDateModule } from '@angular/material/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { ApiModule } from '@api/client';
import { InputMaskModule } from '@ngneat/input-mask';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { SharedStore } from '@shared/store/shared.signal-store';
import {
  BaseAuthService,
  BaseCurrentUserStore,
  WAIT_TO_RESOLVE_ON_TIMEOUT,
} from '@stillmandigital/common';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { apiConfigurationProvider } from './auth/shared/services/api-config.factory';
import { AuthInterceptor } from './auth/shared/services/auth.interceptor';
import { AuthService } from './auth/shared/services/auth.service';

import version from '../version.json';

const LOADING_NOTIFICATION_DURATION_MS = 10_000;

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', `.json?v=${version.version}`);
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    ApiModule,
    InputMaskModule,
  ],
  providers: [
    apiConfigurationProvider,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: BaseAuthService,
      useExisting: AuthService,
    },
    {
      provide: BaseCurrentUserStore,
      useExisting: SharedStore,
    },
    {
      provide: WAIT_TO_RESOLVE_ON_TIMEOUT,
      useFactory: () => {
        const notificationService = inject(NotificationService);

        return () =>
          notificationService.showErrorNotification(
            'NOTIFICATION.LOADING_IS_TAKING_LONGER_THAN_USUAL',
            undefined,
            undefined,
            LOADING_NOTIFICATION_DURATION_MS,
          );
      },
    },
    importProvidersFrom(MatNativeDateModule),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      // Eagerly create SharedStore so the app-version poller and the language
      // bootstrap run without a component having to touch them.
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [SharedStore],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
